import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Megaphone, Copy } from "lucide-react";

export const PromoMessageInput = ({ value, onChange, testId }) => (
  <div className="space-y-2">
    <Label>Menu message (optional)</Label>
    <Input
      value={value}
      maxLength={200}
      onChange={(e) => onChange(e.target.value)}
      placeholder="All 16in pizzas $19.99 Mondays"
      data-testid={testId}
    />
    <p className="text-xs text-muted-foreground">
      Shown on the store menu only while the promo is live: a banner at the top of every page and a note on each promoted item.
    </p>
  </div>
);

const snippetFor = (site) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/promo-messages/${site.id}`;
  return `function kp_promo_messages() {
    $data = get_transient('kp_promo_msgs');
    if ($data === false) {
        $res = wp_remote_get('${url}', ['timeout' => 4]);
        $json = is_wp_error($res) ? null : json_decode(wp_remote_retrieve_body($res), true);
        $data = isset($json['messages']) ? $json['messages'] : [];
        set_transient('kp_promo_msgs', $data, 2 * MINUTE_IN_SECONDS);
    }
    return $data;
}
add_action('wp_body_open', function () {
    $msgs = kp_promo_messages();
    if (!$msgs) return;
    echo '<div class="kp-promo-banner" style="background:#c8102e;color:#fff;text-align:center;padding:10px 16px;font-weight:600;">'
        . esc_html(implode('  •  ', array_column($msgs, 'text'))) . '</div>';
});
function kp_product_note() {
    global $product;
    if (!$product) return;
    foreach (kp_promo_messages() as $m) {
        if (in_array($product->get_id(), $m['product_ids'])) {
            echo '<p class="kp-promo-note" style="color:#c8102e;font-weight:600;margin:4px 0;">' . esc_html($m['text']) . '</p>';
        }
    }
}
add_action('woocommerce_after_shop_loop_item_title', 'kp_product_note', 15);
add_action('woocommerce_single_product_summary', 'kp_product_note', 11);`;
};

export const MenuSnippetButton = ({ site }) => {
  const [open, setOpen] = useState(false);
  const code = snippetFor(site);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`menu-snippet-${site.id}`}>
        <Megaphone className="w-3.5 h-3.5" /> Menu message
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="menu-snippet-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Show promo messages on {site.name}</DialogTitle>
          </DialogHeader>
          <ol className="text-sm space-y-1 list-decimal pl-5 text-muted-foreground">
            <li>In this store's WordPress admin, install and activate the free <b>Code Snippets</b> plugin.</li>
            <li>Go to <b>Snippets → Add New</b>, paste the code below and choose <b>Run snippet everywhere</b>.</li>
            <li>Click <b>Save and Activate</b>. This is a one-time step per store.</li>
          </ol>
          <pre className="text-[11px] bg-muted/40 border border-border rounded-md p-3 overflow-x-auto whitespace-pre" data-testid="menu-snippet-code">{code}</pre>
          <Button onClick={copy} data-testid="menu-snippet-copy"><Copy className="w-4 h-4" /> Copy code</Button>
          <p className="text-xs text-muted-foreground">
            This code is unique to this store. Messages appear within about 2 minutes of a promo going live (longer if the site uses a page cache).
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};
